import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue, SpecraBadge, SpecraButton, SpecraCombobox, SpecraEntityCell, SpecraFilterToolbar, SpecraInput, SpecraSelect } from '@mono/ui-components';
import { Testids } from '@mono/ui-selectors';
import { useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { registryRecordHref } from '../../../components/SharedRecord';
import { rateTypeVariant } from '../../Registries/badgeVariants';
import { RegistryListNote } from '../../Registries/list/RegistryListNote';
import { useShowMore } from '../../Registries/list/useShowMore';
import { useRegistryRecordsEnabled } from '../../Registries/registryFlags';
import { registryHref } from '../../Registries/registryHref';
import { EntityTable } from '../_shared/components/EntityTable';
import { useTenantId } from '../_shared/hooks/useTenantId';
import { BURDEN_LABELS, companyProfileHooks, contractVehicleHooks, laborCategoryHooks, RATE_TYPE_LABELS, useLaborCategoryAnchors, useRateObservations } from './hooks';
import { RateObservationHistory } from './RateObservationHistory';
import { periodDatesLabel, rateAssumptionNotes } from './rateApplicability';
const ANY = '__any__';
const UNITS = [
    'HOURLY',
    'DAILY',
    'MONTHLY',
    'ANNUAL',
    'FIXED',
    'ITERATION'
];
const TYPES = {
    ...RATE_TYPE_LABELS,
    FLOOR: 'Floor (minimum)',
    AWARDED: 'Awarded'
};
// Money with its unit; an unstated amount context is never rendered as $0.
const amountLabel = (r)=>`${r.currency && r.currency !== 'USD' ? `${r.currency} ` : '$'}${r.amount.toFixed(2)}/${(r.rateUnit || 'unit unknown').toLowerCase().replace('hourly', 'hr')}`;
const burdenLabel = (r)=>BURDEN_LABELS[r.burdenBasis] ?? 'burden not stated';
const periodLabel = (r)=>`${r.periodLabel || 'period not stated'} · ${periodDatesLabel(r)}`;
const applicability = (r)=>{
    const parts = [
        r.worksite ? `worksite ${r.worksite}` : 'worksite unknown',
        r.locality ? `locality ${r.locality}` : 'locality unknown',
        ...rateAssumptionNotes(r)
    ];
    return parts.join('; ');
};
function Filter({ label, testid, value, options, onChange }) {
    return /*#__PURE__*/ _jsxs(Select, {
        value: value || ANY,
        onValueChange: (v)=>onChange(v === ANY ? '' : v),
        children: [
            /*#__PURE__*/ _jsx(SelectTrigger, {
                className: "h-9 w-44 text-xs",
                "aria-label": label,
                "data-testid": testid,
                children: /*#__PURE__*/ _jsx(SelectValue, {})
            }),
            /*#__PURE__*/ _jsxs(SelectContent, {
                children: [
                    /*#__PURE__*/ _jsxs(SelectItem, {
                        value: ANY,
                        children: [
                            label,
                            ": any"
                        ]
                    }),
                    options.map((o)=>/*#__PURE__*/ _jsx(SelectItem, {
                            value: o.value,
                            children: o.label
                        }, o.value))
                ]
            })
        ]
    });
}
// Tenant-wide, read-only research surface over the SAME observations the LCAT
// drawer and Compare read. Authorization lives in ryugu: a viewer without
// registry authority gets an error here, never a count or a hint.
export function RateBookTab() {
    const recordsEnabled = useRegistryRecordsEnabled();
    const [anchorSearch, setAnchorSearch] = useState('');
    const tenantId = useTenantId();
    const anchors = useLaborCategoryAnchors(tenantId, anchorSearch, {
        enabled: recordsEnabled
    });
    const [params, setParams] = useSearchParams();
    const get = (k)=>params.get(k) ?? '';
    const set = (k, v)=>setParams((prev)=>{
            const next = new URLSearchParams(prev);
            if (v) next.set(k, v);
            else next.delete(k);
            return next;
        }, {
            replace: true
        });
    const company = get('company');
    const vehicle = get('vehicle');
    const lcat = get('lcat');
    const type = get('type');
    const unit = get('unit');
    const burden = get('burden');
    const period = get('period');
    const selectedId = get('selected');
    const companies = companyProfileHooks.useList({
        companyId: tenantId
    }).data?.items ?? [];
    const vehicles = contractVehicleHooks.useList({
        companyId: tenantId
    }).data?.items ?? [];
    const entries = laborCategoryHooks.useList({
        companyId: tenantId,
        includeRetired: true
    }).data?.items ?? [];
    const query = useRateObservations(tenantId, {
        lcatEntryId: lcat || undefined,
        ...recordsEnabled ? {
            anchorId: get('anchor') || undefined
        } : {},
        companyProfileId: company || undefined,
        contractVehicleId: vehicle || undefined,
        observationType: type || undefined,
        rateUnit: unit || undefined,
        burdenBasis: burden || undefined,
        normalizedPeriodKey: period || undefined,
        acrossVersions: !!lcat,
        limit: 500
    });
    // The version-chain read only narrows by LCAT and type, so the remaining
    // filters apply here when an LCAT is chosen; the plain read filters server-side.
    const rows = (query.data ?? []).filter((r)=>!lcat || (!company || r.companyProfileId === company) && (!vehicle || r.contractVehicleId === vehicle) && (!unit || r.rateUnit === unit) && (!burden || r.burdenBasis === burden) && (!period || r.normalizedPeriodKey === period));
    const paging = useShowMore(rows, JSON.stringify([
        company,
        vehicle,
        lcat,
        type,
        unit,
        burden,
        period,
        get('anchor')
    ]));
    const origin = `/registries?${params}`;
    const linked = (kind, id, label)=>{
        const href = registryHref({
            kind,
            id
        }, origin);
        return href ? /*#__PURE__*/ _jsx(Link, {
            to: href,
            children: label
        }) : label;
    };
    const selected = rows.find((r)=>r.id === selectedId) ?? null;
    // History = the correction chain around the selected row, newest included.
    const historyQuery = useRateObservations(tenantId, {
        lcatEntryId: selected?.lcatEntryId,
        acrossVersions: true,
        includeCorrected: true
    }, {
        enabled: !!selected
    });
    const chain = (()=>{
        if (!selected) return [];
        const all = historyQuery.data ?? [
            selected
        ];
        const ids = new Set([
            selected.id
        ]);
        for(let grew = true; grew;){
            grew = false;
            for (const r of all){
                const linked = [
                    r.correctsId,
                    r.replacesId,
                    r.replacementId
                ].filter(Boolean);
                if (!ids.has(r.id) && linked.some((l)=>ids.has(l)) || ids.has(r.id) && linked.some((l)=>!ids.has(l))) {
                    ids.add(r.id);
                    linked.forEach((l)=>ids.add(l));
                    grew = true;
                }
            }
        }
        return all.filter((r)=>ids.has(r.id));
    })();
    const columns = [
        {
            key: 'amount',
            header: 'Amount',
            render: (r)=>/*#__PURE__*/ _jsx("span", {
                    className: "font-medium",
                    children: amountLabel(r)
                })
        },
        {
            key: 'type',
            header: 'Type',
            render: (r)=>TYPES[r.observationType] ?? r.observationType
        },
        {
            key: 'lcat',
            header: 'Source LCAT',
            render: (r)=>r.lcatTitle || 'LCAT not resolved'
        },
        {
            key: 'company',
            header: 'Company',
            render: (r)=>r.companyName || 'company not stated'
        },
        {
            key: 'vehicle',
            header: 'Vehicle',
            render: (r)=>r.vehicleName || 'vehicle not stated'
        },
        {
            key: 'burden',
            header: 'Burden',
            render: burdenLabel
        },
        {
            key: 'period',
            header: 'Period',
            render: periodLabel
        },
        {
            key: 'source',
            header: 'Source',
            render: (r)=>r.source || 'source not stated'
        },
        {
            key: 'applicability',
            header: 'Applicability',
            render: applicability
        }
    ];
    const recordColumns = [
        {
            key: 'company',
            header: 'Company',
            render: (r)=>linked('COMPANY', r.companyProfileId, r.companyName || '—')
        },
        {
            key: 'lcat',
            header: 'Labor category',
            render: (r)=>/*#__PURE__*/ _jsx(SpecraEntityCell, {
                    title: linked('LCAT_ENTRY', r.lcatEntryId, r.lcatTitle || '—'),
                    description: r.mappedAnchorTitle || r.mappedRelation || undefined
                })
        },
        {
            key: 'vehicle',
            header: 'Vehicle',
            render: (r)=>/*#__PURE__*/ _jsx(SpecraEntityCell, {
                    title: linked('VEHICLE', r.effectiveVehicleId || r.contractVehicleId, r.effectiveVehicleName || r.vehicleName || '—'),
                    description: !r.contractVehicleId && r.effectiveVehicleId && r.sourceLabel ? `via ${r.sourceLabel}` : undefined
                })
        },
        {
            key: 'type',
            header: 'Type',
            render: (r)=>/*#__PURE__*/ _jsx(SpecraBadge, {
                    variant: rateTypeVariant(r.observationType),
                    children: RATE_TYPE_LABELS[r.observationType] || r.observationType
                })
        },
        {
            key: 'amount',
            header: 'Rate',
            align: 'right',
            render: amountLabel
        },
        {
            key: 'burden',
            header: 'Burden',
            render: (r)=>!r.burdenBasis || r.burdenBasis === 'UNKNOWN' ? 'not stated' : burdenLabel(r)
        },
        {
            key: 'period',
            header: 'Period',
            render: (r)=>/*#__PURE__*/ _jsx(SpecraEntityCell, {
                    title: r.periodLabel ? `“${r.periodLabel}”` : 'not stated',
                    description: r.resolvedNormalizedPeriodKey || r.normalizedPeriodKey || undefined
                })
        },
        {
            key: 'source',
            header: 'Source',
            render: (r)=>r.sourceLabel || '—'
        }
    ];
    return /*#__PURE__*/ _jsxs("div", {
        className: "space-y-4",
        children: [
            recordsEnabled ? /*#__PURE__*/ _jsx(SpecraFilterToolbar, {
                search: false,
                filters: /*#__PURE__*/ _jsxs(_Fragment, {
                    children: [
                        /*#__PURE__*/ _jsx(SpecraCombobox, {
                            label: "Company",
                            placeholder: "Company: any",
                            searchPlaceholder: "Search companies",
                            clearable: true,
                            clearLabel: "Any company",
                            value: company || undefined,
                            options: companies.map((c)=>({
                                    value: c.id,
                                    label: c.name
                                })),
                            onValueChange: (v)=>set('company', v || ''),
                            triggerProps: {
                                'data-testid': Testids.deliveryRegistries.rateBookCompanyFilter
                            }
                        }),
                        /*#__PURE__*/ _jsx(SpecraCombobox, {
                            label: "Vehicle",
                            placeholder: "Vehicle: any",
                            searchPlaceholder: "Search vehicles",
                            clearable: true,
                            clearLabel: "Any vehicle",
                            value: vehicle || undefined,
                            options: vehicles.map((v)=>({
                                    value: v.id,
                                    label: v.name
                                })),
                            onValueChange: (v)=>set('vehicle', v || ''),
                            triggerProps: {
                                'data-testid': Testids.deliveryRegistries.rateBookVehicleFilter
                            }
                        }),
                        /*#__PURE__*/ _jsx(SpecraCombobox, {
                            label: "Labor category",
                            placeholder: "Labor category: any",
                            searchPlaceholder: "Search labor categories",
                            clearable: true,
                            clearLabel: "Any labor category",
                            value: get('anchor') || undefined,
                            options: (anchors.data ?? []).map((a)=>({
                                    value: a.id,
                                    label: a.title
                                })),
                            onValueChange: (v)=>set('anchor', v || ''),
                            onSearchValueChange: setAnchorSearch,
                            shouldFilter: false,
                            loading: anchors.isLoading,
                            triggerProps: {
                                'data-testid': Testids.deliveryRegistries.rateBookAnchorFilter
                            }
                        }),
                        /*#__PURE__*/ _jsxs(SpecraSelect, {
                            size: "sm",
                            className: "w-auto",
                            "aria-label": "Type",
                            value: type,
                            onChange: (e)=>set('type', e.target.value),
                            children: [
                                /*#__PURE__*/ _jsx("option", {
                                    value: "",
                                    children: "Type: any"
                                }),
                                Object.entries(RATE_TYPE_LABELS).map(([value, label])=>/*#__PURE__*/ _jsx("option", {
                                        value: value,
                                        children: label
                                    }, value))
                            ]
                        })
                    ]
                })
            }) : /*#__PURE__*/ _jsxs("div", {
                className: "flex flex-wrap items-center gap-2",
                children: [
                    /*#__PURE__*/ _jsx(Filter, {
                        label: "Company",
                        testid: Testids.deliveryRegistries.rateBookFilterCompany,
                        value: company,
                        options: companies.map((c)=>({
                                value: c.id,
                                label: c.name
                            })),
                        onChange: (v)=>set('company', v)
                    }),
                    /*#__PURE__*/ _jsx(Filter, {
                        label: "Vehicle",
                        testid: Testids.deliveryRegistries.rateBookFilterVehicle,
                        value: vehicle,
                        options: vehicles.map((v)=>({
                                value: v.id,
                                label: v.name
                            })),
                        onChange: (v)=>set('vehicle', v)
                    }),
                    /*#__PURE__*/ _jsx(Filter, {
                        label: "Source LCAT",
                        testid: Testids.deliveryRegistries.rateBookFilterLcat,
                        value: lcat,
                        options: entries.map((e)=>({
                                value: e.id,
                                label: e.title
                            })),
                        onChange: (v)=>set('lcat', v)
                    }),
                    /*#__PURE__*/ _jsx(Filter, {
                        label: "Rate type",
                        testid: Testids.deliveryRegistries.rateBookFilterType,
                        value: type,
                        options: Object.entries(TYPES).map(([value, label])=>({
                                value,
                                label
                            })),
                        onChange: (v)=>set('type', v)
                    }),
                    /*#__PURE__*/ _jsx(Filter, {
                        label: "Unit",
                        testid: Testids.deliveryRegistries.rateBookFilterUnit,
                        value: unit,
                        options: UNITS.map((u)=>({
                                value: u,
                                label: u.toLowerCase()
                            })),
                        onChange: (v)=>set('unit', v)
                    }),
                    /*#__PURE__*/ _jsx(Filter, {
                        label: "Burden",
                        testid: Testids.deliveryRegistries.rateBookFilterBurden,
                        value: burden,
                        options: Object.entries(BURDEN_LABELS).map(([value, label])=>({
                                value,
                                label
                            })),
                        onChange: (v)=>set('burden', v)
                    }),
                    /*#__PURE__*/ _jsx(SpecraInput, {
                        className: "h-9 w-40 text-xs",
                        "aria-label": "Period key",
                        placeholder: "Period key",
                        "data-testid": Testids.deliveryRegistries.rateBookFilterPeriod,
                        value: period,
                        onChange: (e)=>set('period', e.target.value)
                    })
                ]
            }),
            query.isError && /*#__PURE__*/ _jsx("p", {
                role: "alert",
                children: "Unable to load the Rate Book."
            }),
            !recordsEnabled && query.isSuccess && /*#__PURE__*/ _jsxs("p", {
                className: "text-xs text-muted-foreground",
                children: [
                    rows.length,
                    " observation",
                    rows.length === 1 ? '' : 's',
                    " you may see. Observed values only — no blended or market rate is computed."
                ]
            }),
            /*#__PURE__*/ _jsx(EntityTable, {
                data: recordsEnabled ? paging.visible : rows,
                columns: recordsEnabled ? recordColumns : columns,
                testid: Testids.deliveryRegistries.rateBookTable,
                rowTestid: Testids.deliveryRegistries.rateBookRow,
                rowKey: (r)=>r.id,
                isLoading: query.isLoading,
                hideRowActions: true,
                onRowClick: (r)=>set('selected', r.id),
                emptyState: /*#__PURE__*/ _jsx("p", {
                    "data-testid": Testids.deliveryRegistries.rateBookEmpty,
                    className: "text-sm",
                    children: "No observations match these filters."
                })
            }),
            recordsEnabled && /*#__PURE__*/ _jsxs(_Fragment, {
                children: [
                    paging.hasMore && /*#__PURE__*/ _jsx(SpecraButton, {
                        variant: "secondary",
                        "data-testid": Testids.deliveryRegistries.showMore,
                        onClick: paging.showMore,
                        children: "Show more"
                    }),
                    /*#__PURE__*/ _jsx(RegistryListNote, {
                        children: "Observed values only: no blended or market rate is computed here. Each row keeps its type, its period as written, and its source."
                    })
                ]
            }),
            selected && /*#__PURE__*/ _jsxs("div", {
                "data-testid": Testids.deliveryRegistries.rateBookDetail,
                className: "space-y-3 rounded-md border p-4",
                children: [
                    /*#__PURE__*/ _jsxs("div", {
                        className: "flex flex-wrap gap-4 text-xs",
                        children: [
                            selected.companyProfileId && /*#__PURE__*/ _jsxs(Link, {
                                className: "text-primary underline-offset-2 hover:underline",
                                "data-testid": Testids.deliveryRegistries.rateBookLinkCompany,
                                to: registryRecordHref('company', selected.companyProfileId, `${window.location.pathname}${window.location.search}`),
                                children: [
                                    "Company: ",
                                    selected.companyName || 'open record'
                                ]
                            }),
                            selected.contractVehicleId && /*#__PURE__*/ _jsxs(Link, {
                                className: "text-primary underline-offset-2 hover:underline",
                                "data-testid": Testids.deliveryRegistries.rateBookLinkVehicle,
                                to: registryRecordHref('vehicle', selected.contractVehicleId, `${window.location.pathname}${window.location.search}`),
                                children: [
                                    "Vehicle: ",
                                    selected.vehicleName || 'open record'
                                ]
                            }),
                            /*#__PURE__*/ _jsxs(Link, {
                                className: "text-primary underline-offset-2 hover:underline",
                                "data-testid": Testids.deliveryRegistries.rateBookLinkLcat,
                                to: registryHref({
                                    kind: 'LCAT_ENTRY',
                                    id: selected.lcatEntryId
                                }, origin) ?? origin,
                                children: [
                                    "Labor category: ",
                                    selected.lcatTitle || 'open record'
                                ]
                            })
                        ]
                    }),
                    /*#__PURE__*/ _jsx(RateObservationHistory, {
                        companyId: tenantId,
                        rates: chain,
                        canEdit: false,
                        entries: [],
                        companies: [],
                        vehicles: []
                    })
                ]
            })
        ]
    });
}
